// src/pages/Dashboard.jsx
import { useEffect, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import { 
  BookOpen, Award, TrendingUp, Clock, ChevronRight, Zap, Trophy, 
  Star, Target, Flame, Crown, Book, Headphones, Play, History, 
  Sparkles, Medal, Gem, Rocket, Camera, X, AlertCircle 
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import useStore from '../store/useStore';
import { courses, podcasts } from '../data/mockData';
import { Users } from 'lucide-react';  

// Helper function to get initials
function getInitials(name) {
  if (!name) return '';
  const names = name.trim().split(' ');
  if (names.length === 1) {
    return names[0].charAt(0).toUpperCase();
  } else {
    return (names[0].charAt(0) + names[names.length - 1].charAt(0)).toUpperCase();
  }
}

// Helper function to format time with decimal places
function formatLearningTime(minutes) {
  if (!minutes || minutes === 0) return '0m';
  
  if (minutes < 1) {
    const seconds = Math.round(minutes * 60);
    return `${seconds}s`;
  } else if (minutes < 60) {
    return `${minutes.toFixed(2)}m`;
  } else {
    const hours = minutes / 60;
    return `${hours.toFixed(2)}h`;
  }
}

function Dashboard() {
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const [showProfileUpload, setShowProfileUpload] = useState(false);
  const [uploadedImage, setUploadedImage] = useState(null);
  const [showContinueListening, setShowContinueListening] = useState(true);
  const [userListeningHistory, setUserListeningHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  
  const { 
    user, 
    enrolledCourses, 
    completedLessons, 
    accessTier, 
    getCourseProgress, 
    enrollInCourse,
    setCurrentlyPlaying,
    userPoints,
    userLevel,
    userRank,
    dailyStreak,
    badges,
    profilePicture,
    setProfilePicture,
    removeProfilePicture,
    learningTime,
    getTotalLearningTime,
    getCompletedPodcastsCount,
    getTotalPodcastsListened,
    getPodcastListeningStats
  } = useStore();

  // Calculate completed lessons count
  const completedLessonsCount = completedLessons ? 
    Object.values(completedLessons).reduce((sum, lessons) => sum + lessons.length, 0) : 0;

// In Dashboard.jsx, find this function and update it:

const fetchListeningHistory = async () => {
  const apiToken = localStorage.getItem('api_token'); // This is already here at the top
  const token = localStorage.getItem('token');
  
  console.log('Fetching listening history...');
  console.log('User ID:', user?.id);
  console.log('Has API Token:', !!apiToken);
  
  if (!apiToken || !user?.id) {
    console.log('Missing api_token or user ID, setting empty history');
    setUserListeningHistory([]);
    return;
  }
  
  setLoadingHistory(true);
  
  // 🔍 ADD JUST THE DEBUG CODE HERE (remove the duplicate const declaration)
  console.log('🔍 Token being sent:', apiToken ? `Bearer ${apiToken.substring(0, 20)}...` : 'none');
  console.log('🔍 Full Authorization header:', `Bearer ${apiToken}`);
  console.log('🔍 Fetch URL:', '/api/v2/admin/podcast-history.php');
  
  try {
    const response = await fetch('/api/v2/admin/podcast-history.php', {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${apiToken}`,
        'Content-Type': 'application/json'
      }
    });
    
    console.log('Response status:', response.status);
    
    if (!response.ok) {
      if (response.status === 401) {
        console.error('Authentication failed - token may be expired');
        setUserListeningHistory([]);
        return;
      }
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    const text = await response.text();
    console.log('Raw response:', text.substring(0, 200));
    
    // Check if response is HTML (error page)
    if (text.trim().startsWith('<!DOCTYPE')) {
      console.error('Received HTML instead of JSON:', text.substring(0, 200));
      setUserListeningHistory([]);
      return;
    }
    
    let data;
    try {
      data = JSON.parse(text);
    } catch (parseError) {
      console.error('Failed to parse JSON:', parseError);
      console.error('Raw response:', text.substring(0, 200));
      setUserListeningHistory([]);
      return;
    }
    
    if (data.success) {
      console.log('History fetched successfully:', data.count, 'items');
      setUserListeningHistory(data.history || []);
    } else {
      console.error('API returned error:', data.error);
      setUserListeningHistory([]);
    }
    
  } catch (error) {
    console.error('Failed to fetch history:', error);
    setUserListeningHistory([]);
  } finally {
    setLoadingHistory(false);
  }
};

  // Fetch history only when user changes
  useEffect(() => {
    setUserListeningHistory([]);
    
    if (user?.id) {
      console.log('User changed, fetching history for user:', user.id);
      fetchListeningHistory();
    } else {
      console.log('No user, clearing history');
    }
  }, [user?.id]);

  // Calculate total learning time
  const totalLearningTime = getTotalLearningTime ? getTotalLearningTime() : learningTime || 0;
  
  // Get podcast stats
  const podcastStats = getPodcastListeningStats ? getPodcastListeningStats() : { 
    completedPodcasts: 0, 
    totalPodcastsListened: 0, 
    averageCompletion: 0 
  };
  const completedPodcastsCount = podcastStats.completedPodcasts || 0;
  const totalPodcastsListened = podcastStats.totalPodcastsListened || 0;
  const avgCompletion = podcastStats.averageCompletion || 0;

  // Enrolled course details
  const enrolledCourseDetails = enrolledCourses
    .map((id) => courses.find((c) => c.id === id))
    .filter(Boolean);

  // Get recent listening history (last 5 podcasts)
  const recentPodcasts = userListeningHistory
    .slice(0, 5)
    .map(history => {
      const podcast = podcasts.find(p => p.id === history.podcastId);
      if (podcast) {
        return {
          ...podcast,
          progress: history.progress || 0,
          completed: history.completed || false,
          lastListened: history.listened_at || history.lastListened
        };
      }
      return null;
    })
    .filter(Boolean);

  // Calculate completed courses count
  const completedCoursesCount = enrolledCourseDetails.filter(course => {
    const totalLessons = course.modules?.reduce((sum, m) => sum + (m.lessons?.length || 0), 0) || 0;
    return getCourseProgress(course.id, totalLessons) === 100;
  }).length;

// In Dashboard.jsx, update this function (around line 170-180)

const getContinueListeningPodcasts = () => {
  if (!userListeningHistory.length || !podcasts) return [];
  
  return userListeningHistory
    .filter(history => {
      const podcast = podcasts.find(p => p.id === history.podcastId);
      if (!podcast || !podcast.duration) return false;
      
      const durationMatch = podcast.duration.match(/(\d+)/);
      if (!durationMatch) return false;
      
      const progress = history.progress || 0;
      
      // FIX: Show podcasts with progress between 1-89% (not just 10-89%)
      // This includes podcasts that were just started (1-9%)
      return progress >= 1 && progress < 90 && !history.completed;
    })
    .map(history => {
      const podcast = podcasts.find(p => p.id === history.podcastId);
      const durationMatch = podcast.duration.match(/(\d+)/);
      const totalMinutes = parseInt(durationMatch[1]);
      const progress = history.progress || 0;
      const listenedMinutes = (progress / 100) * totalMinutes;
      const remainingMinutes = Math.ceil(totalMinutes - listenedMinutes);
      
      return {
        ...podcast,
        progressPercentage: progress,
        listenedMinutes: parseFloat(listenedMinutes.toFixed(2)),
        totalMinutes,
        remainingMinutes,
        lastListened: history.listened_at || history.lastListened
      };
    })
    .filter(Boolean)
    .sort((a, b) => new Date(b.lastListened) - new Date(a.lastListened))
    .slice(0, 3);
};

  const continueListeningPodcasts = getContinueListeningPodcasts();

  // Enhanced achievements data
  const achievements = [
    {
      id: 1,
      icon: '🎯',
      title: 'First Steps',
      description: 'Complete your first lesson or podcast',
      earned: completedLessonsCount > 0 || completedPodcastsCount > 0,
      points: 50,
      rarity: 'Common',
      type: 'lesson',
      progress: completedLessonsCount > 0 ? 1 : (completedPodcastsCount > 0 ? 1 : 0),
      total: 1
    },
    {
      id: 2,
      icon: '🔥',
      title: 'Week Warrior',
      description: 'Learn 7 days in a row',
      earned: dailyStreak >= 7,
      points: 100,
      rarity: 'Rare',
      type: 'streak',
      progress: dailyStreak,
      total: 7
    },
    {
      id: 3,
      icon: '🏆',
      title: 'Course Champion',
      description: 'Complete your first course',
      earned: completedCoursesCount > 0,
      points: 200,
      rarity: 'Epic',
      type: 'course',
      progress: completedCoursesCount > 0 ? 1 : 0,
      total: 1
    },
    {
      id: 4,
      icon: '🎧',
      title: 'Podcast Pro',
      description: 'Complete 10 podcasts',
      earned: completedPodcastsCount >= 10,
      points: 150,
      rarity: 'Rare',
      type: 'podcast',
      progress: completedPodcastsCount,
      total: 10
    },
    {
      id: 5,
      icon: '💎',
      title: 'Attentive Listener',
      description: '85%+ average podcast completion',
      earned: avgCompletion >= 85 && totalPodcastsListened >= 5,
      points: 200,
      rarity: 'Epic',
      type: 'quality',
      progress: avgCompletion,
      total: 85
    },
    {
      id: 6,
      icon: '⭐',
      title: 'Point Master',
      description: 'Reach 5,000 points',
      earned: userPoints >= 5000,
      points: 250,
      rarity: 'Legendary',
      type: 'points',
      progress: userPoints,
      total: 5000
    }
  ];

  const earnedAchievements = achievements.filter(a => a.earned);
  const lockedAchievements = achievements.filter(a => !a.earned);

  const stats = [
    {
      icon: BookOpen,
      label: 'Enrolled Courses',
      value: enrolledCourses.length,
      color: 'from-blue-500 to-blue-600'
    },
    {
      icon: Award,
      label: 'Completed Lessons',
      value: completedLessonsCount,
      color: 'from-purple-500 to-purple-600'
    },
    {
      icon: Headphones,
      label: 'Podcasts Completed',
      value: completedPodcastsCount,
      color: 'from-green-500 to-green-600'
    },
    {
      icon: Clock,
      label: 'Learning Time',
      value: formatLearningTime(totalLearningTime),
      color: 'from-orange-500 to-orange-600'
    }
  ];

  const handlePlayPodcast = (podcast) => {
    setCurrentlyPlaying(podcast);
    navigate('/podcasts');
  };

  // Profile picture handlers
  const handleProfilePictureClick = () => {
    setShowProfileUpload(true);
  };

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        alert('Please select an image smaller than 5MB');
        return;
      }
      
      const reader = new FileReader();
      reader.onload = (e) => {
        setUploadedImage(e.target.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSaveProfilePicture = () => {
    if (uploadedImage) {
      setProfilePicture(uploadedImage);
      setShowProfileUpload(false);
      setUploadedImage(null);
    }
  };

  const handleRemoveProfilePicture = () => {
    removeProfilePicture();
    setShowProfileUpload(false);
    setUploadedImage(null);
  };

  const handleCancelUpload = () => {
    setShowProfileUpload(false);
    setUploadedImage(null);
  };

  // Badge helper
  const getBadgeInfo = (badgeId) => {
    const badgeMap = {
      'first_steps': {
        icon: '🎯',
        title: 'First Steps',
        description: 'Completed first lesson or podcast',
        rarity: 'Common'
      },
      'week_warrior': {
        icon: '🔥',
        title: 'Week Warrior',
        description: '7-day learning streak',
        rarity: 'Rare'
      },
      'month_master': {
        icon: '⭐',
        title: 'Month Master',
        description: '30-day learning streak',
        rarity: 'Epic'
      },
      'point_milestone_1k': {
        icon: '🏆',
        title: 'Point Collector',
        description: 'Reached 1,000 points',
        rarity: 'Rare'
      },
      'point_milestone_5k': {
        icon: '💎',
        title: 'Point Master',
        description: 'Reached 5,000 points',
        rarity: 'Epic'
      },
      'point_milestone_10k': {
        icon: '👑',
        title: 'Point Legend',
        description: 'Reached 10,000 points',
        rarity: 'Legendary'
      },
      'podcast_listener': {
        icon: '🎧',
        title: 'Podcast Listener',
        description: 'Completed first podcast',
        rarity: 'Common'
      },
      'podcast_pro': {
        icon: '📻',
        title: 'Podcast Pro',
        description: 'Completed 10 podcasts',
        rarity: 'Rare'
      },
      'podcast_master': {
        icon: '🎙️',
        title: 'Podcast Master',
        description: 'Completed 50 podcasts',
        rarity: 'Epic'
      },
      'attentive_listener': {
        icon: '👂',
        title: 'Attentive Listener',
        description: '85%+ average completion',
        rarity: 'Epic'
      },
      'course_completer': {
        icon: '📚',
        title: 'Course Completer',
        description: 'Completed first course',
        rarity: 'Rare'
      }
    };
    
    return badgeMap[badgeId] || {
      icon: '🛡️',
      title: badgeId.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()),
      description: 'Achievement unlocked!',
      rarity: 'Common'
    };
  };

  const getRarityColor = (rarity) => {
    const colors = {
      'Common': 'bg-gray-100 text-gray-800',
      'Rare': 'bg-blue-100 text-blue-800',
      'Epic': 'bg-purple-100 text-purple-800',
      'Legendary': 'bg-yellow-100 text-yellow-800'
    };
    return colors[rarity] || 'bg-gray-100 text-gray-800';
  };

  const getAchievementProgress = (achievement) => {
    if (achievement.earned) return 'Completed';
    
    switch (achievement.type) {
      case 'lesson':
        return `${achievement.progress}/1 lesson`;
      case 'streak':
        return `${achievement.progress}/7 days`;
      case 'course':
        return `${achievement.progress}/1 course`;
      case 'podcast':
        return `${achievement.progress}/10 podcasts`;
      case 'points':
        return `${achievement.progress}/5000 points`;
      case 'quality':
        return `${achievement.progress.toFixed(1)}%/85%`;
      default:
        return 'In progress';
    }
  };

  const getAchievementProgressPercentage = (achievement) => {
    if (achievement.earned) return 100;
    
    switch (achievement.type) {
      case 'lesson':
        return achievement.progress > 0 ? 100 : 0;
      case 'streak':
        return Math.min((achievement.progress / 7) * 100, 100);
      case 'course':
        return achievement.progress > 0 ? 100 : 0;
      case 'podcast':
        return Math.min((achievement.progress / 10) * 100, 100);
      case 'points':
        return Math.min((achievement.progress / 5000) * 100, 100);
      case 'quality':
        return Math.min((achievement.progress / 85) * 100, 100);
      default:
        return 0;
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        {/* Welcome Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-12"
        >
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-2">
            Welcome back, {user?.name?.split(' ')[0] || 'Learner'}! 👋
          </h1>
          <p className="text-xl text-gray-600">
            Track your progress and continue your learning journey
          </p>
        </motion.div>

        {/* Enhanced Profile Card */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-gradient-to-br from-blue-600 via-purple-600 to-indigo-600 rounded-2xl p-6 text-white mb-8 shadow-xl"
        >
          <div className="flex flex-col md:flex-row items-start md:items-center justify-between">
            <div className="flex items-center space-x-4 mb-4 md:mb-0">
              <div className="relative">
                <div 
                  className="w-20 h-20 bg-white/20 rounded-full flex items-center justify-center text-3xl font-bold overflow-hidden cursor-pointer hover:bg-white/30 transition-all duration-200 border-3 border-white/50 shadow-lg"
                  onClick={handleProfilePictureClick}
                >
                  {profilePicture ? (
                    <img 
                      src={profilePicture} 
                      alt="Profile" 
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <span>{user?.name ? getInitials(user.name) : '👤'}</span>
                  )}
                </div>
                <div className="absolute -bottom-1 -right-1 bg-yellow-400 text-yellow-900 rounded-full px-2.5 py-1 text-xs font-bold shadow-lg">
                  Lvl {userLevel}
                </div>
                <button 
                  onClick={handleProfilePictureClick}
                  className="absolute -top-1 -right-1 bg-white rounded-full p-1.5 shadow-lg hover:scale-110 transition-transform duration-200"
                >
                  <Camera className="w-4 h-4 text-blue-600" />
                </button>
              </div>
              <div>
                <h2 className="text-2xl md:text-3xl font-bold">{user?.name || 'Alex Johnson'}</h2>
                <div className="flex items-center space-x-3 mt-2">
                  <div className="flex items-center space-x-1 bg-white/20 backdrop-blur-sm px-3 py-1 rounded-full">
                    <Crown className="w-4 h-4 text-yellow-300" />
                    <span className="font-medium text-sm">{userRank}</span>
                  </div>
                  <div className="flex items-center space-x-1 bg-white/20 backdrop-blur-sm px-3 py-1 rounded-full">
                    <Flame className="w-4 h-4 text-orange-300" />
                    <span className="font-medium text-sm">{dailyStreak} day streak</span>
                  </div>
                </div>
              </div>
            </div>
            <div className="flex items-center space-x-6 bg-white/10 backdrop-blur-sm px-6 py-3 rounded-2xl">
              <div className="flex items-center space-x-2">
                <Sparkles className="w-6 h-6 text-yellow-300" />
                <div>
                  <div className="text-2xl font-bold">{userPoints.toLocaleString()}</div>
                  <div className="text-xs text-blue-100">Total Points</div>
                </div>
              </div>
              <div className="h-10 w-px bg-white/20"></div>
              <div>
                <div className="text-2xl font-bold">{badges.length}</div>
                <div className="text-xs text-blue-100">Badges</div>
              </div>
            </div>
          </div>
          
          {/* Progress to next level */}
          <div className="mt-6 bg-white/10 backdrop-blur-sm rounded-xl p-4">
            <div className="flex justify-between text-sm mb-2">
              <span className="font-medium">Progress to Level {userLevel + 1}</span>
              <span className="font-bold">{userPoints % 1000}/1000 XP</span>
            </div>
            <div className="w-full h-2.5 bg-white/20 rounded-full overflow-hidden">
              <div 
                className="h-full bg-gradient-to-r from-yellow-400 to-yellow-300 rounded-full transition-all duration-500"
                style={{ width: `${((userPoints % 1000) / 1000) * 100}%` }}
              />
            </div>
          </div>
        </motion.div>

        {/* Profile Picture Upload Modal */}
        {showProfileUpload && (
          <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              className="bg-white rounded-2xl p-6 max-w-md w-full shadow-2xl"
            >
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-2xl font-bold text-gray-900">Profile Picture</h3>
                <button
                  onClick={handleCancelUpload}
                  className="p-2 hover:bg-gray-100 rounded-full transition-colors duration-200"
                >
                  <X className="w-5 h-5 text-gray-600" />
                </button>
              </div>
              
              <div className="space-y-6">
                <div className="flex justify-center">
                  <div className="w-32 h-32 rounded-full bg-gradient-to-br from-blue-100 to-purple-100 overflow-hidden flex items-center justify-center border-4 border-white shadow-xl">
                    {uploadedImage ? (
                      <img 
                        src={uploadedImage} 
                        alt="Preview" 
                        className="w-full h-full object-cover"
                      />
                    ) : profilePicture ? (
                      <img 
                        src={profilePicture} 
                        alt="Current Profile" 
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <span className="text-5xl text-gray-500">
                        {user?.name ? getInitials(user.name) : '👤'}
                      </span>
                    )}
                  </div>
                </div>
                
                <input
                  type="file"
                  ref={fileInputRef}
                  onChange={handleFileSelect}
                  accept="image/*"
                  className="hidden"
                />
                
                <div className="space-y-3">
                  <button
                    onClick={() => fileInputRef.current?.click()}
                    className="w-full py-3 bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-xl font-semibold hover:from-blue-600 hover:to-blue-700 transition-all duration-200 shadow-md"
                  >
                    Choose Image
                  </button>
                  
                  <div className="flex space-x-3">
                    {uploadedImage && (
                      <button
                        onClick={handleSaveProfilePicture}
                        className="flex-1 py-3 bg-gradient-to-r from-green-500 to-green-600 text-white rounded-xl font-semibold hover:from-green-600 hover:to-green-700 transition-all duration-200 shadow-md"
                      >
                        Save
                      </button>
                    )}
                    {profilePicture && (
                      <button
                        onClick={handleRemoveProfilePicture}
                        className="flex-1 py-3 bg-gradient-to-r from-red-500 to-red-600 text-white rounded-xl font-semibold hover:from-red-600 hover:to-red-700 transition-all duration-200 shadow-md"
                      >
                        Remove
                      </button>
                    )}
                  </div>
                  
                  <button
                    onClick={handleCancelUpload}
                    className="w-full py-3 bg-gray-200 text-gray-700 rounded-xl font-semibold hover:bg-gray-300 transition-colors duration-200"
                  >
                    Cancel
                  </button>
                </div>
                
                <p className="text-xs text-gray-500 text-center">
                  Maximum file size: 5MB. Supported formats: JPG, PNG, GIF
                </p>
              </div>
            </motion.div>
          </div>
        )}

        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
          {stats.map((stat, index) => {
            const Icon = stat.icon;
            return (
              <motion.div
                key={stat.label}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                className="bg-white rounded-xl shadow-lg p-6 hover:shadow-xl transition-shadow duration-200"
              >
                <div className={`w-14 h-14 bg-gradient-to-br ${stat.color} rounded-xl flex items-center justify-center mb-4 shadow-lg`}>
                  <Icon className="w-7 h-7 text-white" />
                </div>
                <div className="text-3xl font-bold text-gray-900 mb-1">{stat.value}</div>
                <div className="text-gray-600 font-medium">{stat.label}</div>
              </motion.div>
            );
          })}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Content - Left & Center */}
          <div className="lg:col-span-2 space-y-8">
            {/* Continue Listening Section */}
            {continueListeningPodcasts.length > 0 && showContinueListening && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
                className="bg-white rounded-xl shadow-lg p-6"
              >
                <div className="flex items-center justify-between mb-6">
                  <div className="flex items-center space-x-2">
                    <History className="w-6 h-6 text-blue-500" />
                    <h2 className="text-2xl font-bold text-gray-900">Continue Listening</h2>
                  </div>
                  <button
                    onClick={() => setShowContinueListening(false)}
                    className="text-sm text-gray-500 hover:text-gray-700"
                  >
                    Hide
                  </button>
                </div>
                
                <div className="space-y-4">
                  {continueListeningPodcasts.map((podcast) => (
                    <div
                      key={podcast.id}
                      onClick={() => handlePlayPodcast(podcast)}
                      className="flex items-center space-x-4 p-4 bg-gray-50 rounded-xl hover:bg-gray-100 transition-all duration-200 cursor-pointer group"
                    >
                      <div className="relative flex-shrink-0">
                        <img 
                          src={podcast.image} 
                          alt={podcast.title}
                          className="w-20 h-20 rounded-xl object-cover group-hover:shadow-md transition-shadow duration-200"
                        />
                        <div className="absolute inset-0 bg-black/40 rounded-xl opacity-0 group-hover:opacity-100 transition-opacity duration-200 flex items-center justify-center">
                          <Play className="w-8 h-8 text-white" />
                        </div>
                      </div>
                      
                      <div className="flex-1 min-w-0">
                        <h3 className="font-bold text-gray-900 text-lg mb-1 truncate">
                          {podcast.title}
                        </h3>
                        <p className="text-sm text-gray-600 mb-2">{podcast.topic}</p>
                        
                        <div className="space-y-2">
                          <div className="flex items-center justify-between text-sm">
                            <span className="text-gray-500">Progress</span>
                            <span className="font-semibold text-blue-600">
                              {Math.round(podcast.progressPercentage)}% • {podcast.remainingMinutes}m left
                            </span>
                          </div>
                          <div className="w-full h-2 bg-gray-200 rounded-full overflow-hidden">
                            <div 
                              className="h-full bg-gradient-to-r from-blue-500 to-purple-500 rounded-full transition-all duration-300"
                              style={{ width: `${podcast.progressPercentage}%` }}
                            />
                          </div>
                        </div>
                      </div>
                      
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handlePlayPodcast(podcast);
                        }}
                        className="p-3 bg-blue-500 text-white rounded-xl hover:bg-blue-600 transition-colors duration-200 shadow-md hover:shadow-lg flex-shrink-0"
                      >
                        <Play className="w-5 h-5" />
                      </button>
                    </div>
                  ))}
                </div>
              </motion.div>
            )}

            {/* Badges & Achievements */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="bg-white rounded-xl shadow-lg p-6"
            >
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center space-x-2">
                  <Trophy className="w-6 h-6 text-yellow-500" />
                  <h2 className="text-2xl font-bold text-gray-900">Badges & Achievements</h2>
                </div>
                <div className="text-right">
                  <div className="text-2xl font-bold text-gray-900">{badges.length}</div>
                  <div className="text-sm text-gray-500">Badges Earned</div>
                </div>
              </div>

              {/* Badges Grid */}
              {badges.length > 0 && (
                <div className="mb-8">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Your Badges</h3>
                  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-4 gap-4">
                    {badges.slice(0, 8).map((badgeId, index) => {
                      const badgeInfo = getBadgeInfo(badgeId);
                      return (
                        <motion.div
                          key={badgeId}
                          initial={{ opacity: 0, scale: 0.9 }}
                          animate={{ opacity: 1, scale: 1 }}
                          transition={{ delay: index * 0.05 }}
                          className="text-center p-4 bg-gradient-to-br from-yellow-50 to-orange-50 rounded-xl border-2 border-yellow-200 hover:shadow-md transition-shadow duration-200"
                        >
                          <div className="text-3xl mb-2">{badgeInfo.icon}</div>
                          <h4 className="font-bold text-gray-900 text-sm mb-1">{badgeInfo.title}</h4>
                          <p className="text-xs text-gray-600 mb-2 line-clamp-1">{badgeInfo.description}</p>
                          <span className={`text-xs px-2 py-1 rounded-full ${getRarityColor(badgeInfo.rarity)}`}>
                            {badgeInfo.rarity}
                          </span>
                        </motion.div>
                      );
                    })}
                    {badges.length > 8 && (
                      <div className="text-center p-4 bg-gray-50 rounded-xl border-2 border-gray-200 flex items-center justify-center">
                        <span className="text-lg font-bold text-gray-600">+{badges.length - 8}</span>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Achievements Progress */}
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center space-x-2">
                  <Target className="w-5 h-5 text-blue-500" />
                  <span>Achievements in Progress</span>
                </h3>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {achievements.filter(a => !a.earned).slice(0, 4).map((achievement) => (
                    <div
                      key={achievement.id}
                      className="flex items-start space-x-3 p-4 bg-gray-50 rounded-xl border border-gray-200"
                    >
                      <div className="text-2xl">{achievement.icon}</div>
                      <div className="flex-1">
                        <div className="flex items-center justify-between mb-1">
                          <h4 className="font-semibold text-gray-900">{achievement.title}</h4>
                          <span className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded-full">
                            +{achievement.points} pts
                          </span>
                        </div>
                        <p className="text-xs text-gray-600 mb-2">{achievement.description}</p>
                        <div className="space-y-1">
                          <div className="flex justify-between text-xs">
                            <span className="text-gray-500">Progress</span>
                            <span className="font-medium text-gray-700">{getAchievementProgress(achievement)}</span>
                          </div>
                          <div className="w-full h-1.5 bg-gray-200 rounded-full overflow-hidden">
                            <div 
                              className="h-full bg-gradient-to-r from-blue-500 to-purple-500 rounded-full"
                              style={{ width: `${getAchievementProgressPercentage(achievement)}%` }}
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                  {lockedAchievements.length === 0 && (
                    <div className="col-span-2 text-center py-8 bg-gradient-to-br from-green-50 to-blue-50 rounded-xl">
                      <Trophy className="w-16 h-16 text-yellow-500 mx-auto mb-4" />
                      <h3 className="text-xl font-bold text-gray-900 mb-2">All Achievements Unlocked! 🎉</h3>
                      <p className="text-gray-600">You're a true learning master. New achievements coming soon!</p>
                    </div>
                  )}
                </div>
              </div>
            </motion.div>

            {/* Recent Podcasts */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
              className="bg-white rounded-xl shadow-lg p-6"
            >
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center space-x-2">
                  <Headphones className="w-6 h-6 text-green-500" />
                  <h2 className="text-2xl font-bold text-gray-900">Recently Played</h2>
                </div>
                <button
                  onClick={() => navigate('/podcasts')}
                  className="text-blue-500 font-medium hover:text-blue-600 transition-colors duration-200"
                >
                  View All
                </button>
              </div>

              {recentPodcasts.length > 0 ? (
                <div className="space-y-3">
                  {recentPodcasts.map((podcast) => (
                    <div
                      key={podcast.id}
                      onClick={() => handlePlayPodcast(podcast)}
                      className="flex items-center space-x-4 p-3 border border-gray-200 rounded-xl hover:bg-gray-50 transition-all duration-200 cursor-pointer group"
                    >
                      <img 
                        src={podcast.image} 
                        alt={podcast.title}
                        className="w-14 h-14 rounded-xl object-cover group-hover:shadow-md transition-shadow duration-200"
                      />
                      <div className="flex-1 min-w-0">
                        <h4 className="font-semibold text-gray-900 truncate">{podcast.title}</h4>
                        <p className="text-sm text-gray-600 truncate">{podcast.topic}</p>
                        <p className="text-xs text-gray-500 mt-1">{podcast.duration}</p>
                      </div>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handlePlayPodcast(podcast);
                        }}
                        className="p-2.5 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors duration-200 shadow-md"
                      >
                        <Play className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12">
                  <Headphones className="w-20 h-20 text-gray-300 mx-auto mb-4" />
                  <h3 className="text-xl font-bold text-gray-900 mb-2">No Podcasts Yet</h3>
                  <p className="text-gray-600 mb-6 max-w-md mx-auto">
                    Start your learning journey by exploring our podcast collection
                  </p>
                  <button
                    onClick={() => navigate('/podcasts')}
                    className="px-8 py-3 bg-gradient-to-r from-blue-500 to-purple-500 text-white rounded-xl font-semibold hover:shadow-lg transition-all duration-200"
                  >
                    Browse Podcasts
                  </button>
                </div>
              )}
            </motion.div>

            {/* Enrolled Courses */}
            <div className="bg-white rounded-xl shadow-lg p-6">
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center space-x-2">
                  <BookOpen className="w-6 h-6 text-purple-500" />
                  <h2 className="text-2xl font-bold text-gray-900">Your Courses</h2>
                </div>
                <button
                  onClick={() => navigate('/courses')}
                  className="text-blue-500 font-medium hover:text-blue-600 transition-colors duration-200"
                >
                  Browse All
                </button>
              </div>

              {enrolledCourseDetails.length === 0 ? (
                <div className="text-center py-12">
                  <BookOpen className="w-20 h-20 text-gray-300 mx-auto mb-4" />
                  <h3 className="text-xl font-bold text-gray-900 mb-2">No Courses Yet</h3>
                  <p className="text-gray-600 mb-6 max-w-md mx-auto">
                    Start your learning journey by enrolling in a course
                  </p>
                  <button
                    onClick={() => navigate('/courses')}
                    className="px-8 py-3 bg-gradient-to-r from-purple-500 to-pink-500 text-white rounded-xl font-semibold hover:shadow-lg transition-all duration-200"
                  >
                    Explore Courses
                  </button>
                </div>
              ) : (
                <div className="space-y-4">
                  {enrolledCourseDetails.map((course) => {
                    const totalLessons = course.modules.reduce((sum, m) => sum + m.lessons.length, 0);
                    const progress = getCourseProgress(course.id, totalLessons);
                    return (
                      <div
                        key={course.id}
                        onClick={() => navigate(`/course/${course.id}`)}
                        className="border border-gray-200 rounded-xl p-4 hover:shadow-md transition-all duration-200 cursor-pointer"
                      >
                        <div className="flex items-center justify-between mb-3">
                          <div className="flex-1">
                            <h3 className="font-bold text-gray-900 text-lg mb-1">{course.title}</h3>
                            <div className="flex items-center space-x-3 text-sm text-gray-600">
                              <span>{course.skillLevel}</span>
                              <span>•</span>
                              <span>{course.duration}</span>
                              {course.hasCertificate && (
                                <>
                                  <span>•</span>
                                  <span className="flex items-center text-yellow-600">
                                    <Award className="w-4 h-4 mr-1" />
                                    Certificate
                                  </span>
                                </>
                              )}
                            </div>
                          </div>
                          <ChevronRight className="w-6 h-6 text-gray-400" />
                        </div>
                        
                        <div className="space-y-2">
                          <div className="flex items-center justify-between text-sm">
                            <span className="text-gray-600">Progress</span>
                            <span className="font-semibold text-blue-600">{progress}%</span>
                          </div>
                          <div className="w-full h-2.5 bg-gray-200 rounded-full overflow-hidden">
                            <motion.div
                              className="h-full bg-gradient-to-r from-blue-500 to-purple-500"
                              initial={{ width: 0 }}
                              animate={{ width: `${progress}%` }}
                              transition={{ duration: 0.5 }}
                            />
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>

          {/* Sidebar - Right Column */}
          <div className="lg:col-span-1 space-y-6">
            {/* Quick Actions */}
            <div className="bg-white rounded-xl shadow-lg p-6">
              <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center space-x-2">
                <Zap className="w-5 h-5 text-yellow-500" />
                <span>Quick Actions</span>
              </h3>
              <div className="space-y-3">
                <button
                  onClick={() => navigate('/podcasts')}
                  className="w-full flex items-center justify-between p-4 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl hover:from-blue-100 hover:to-indigo-100 transition-all duration-200 group"
                >
                  <div className="flex items-center space-x-3">
                    <Headphones className="w-5 h-5 text-blue-600" />
                    <span className="font-medium text-gray-900">Browse Podcasts</span>
                  </div>
                  <ChevronRight className="w-5 h-5 text-gray-500 group-hover:translate-x-1 transition-transform duration-200" />
                </button>
                
                <button
                  onClick={() => navigate('/courses')}
                  className="w-full flex items-center justify-between p-4 bg-gradient-to-r from-purple-50 to-pink-50 rounded-xl hover:from-purple-100 hover:to-pink-100 transition-all duration-200 group"
                >
                  <div className="flex items-center space-x-3">
                    <BookOpen className="w-5 h-5 text-purple-600" />
                    <span className="font-medium text-gray-900">Explore Courses</span>
                  </div>
                  <ChevronRight className="w-5 h-5 text-gray-500 group-hover:translate-x-1 transition-transform duration-200" />
                </button>
                
                {accessTier === 'free' && (
                  <button
                    onClick={() => navigate('/ambassador-program')}
                    className="w-full flex items-center justify-between p-4 bg-gradient-to-r from-yellow-50 to-orange-50 rounded-xl hover:from-yellow-100 hover:to-orange-100 transition-all duration-200 group"
                  >
                    <div className="flex items-center space-x-3">
                      <Award className="w-5 h-5 text-yellow-600" />
                      <span className="font-medium text-gray-900">Ambassador Program</span>
                    </div>
                    <ChevronRight className="w-5 h-5 text-gray-500 group-hover:translate-x-1 transition-transform duration-200" />
                  </button>
                )}
              </div>
            </div>

            {/* Detailed Stats */}
            <div className="bg-white rounded-xl shadow-lg p-6">
              <h3 className="text-xl font-bold text-gray-900 mb-4">Learning Stats</h3>
              <div className="space-y-4">
                <div className="flex justify-between items-center p-3 bg-gray-50 rounded-xl">
                  <span className="text-gray-600">Courses Completed</span>
                  <span className="font-bold text-gray-900 text-lg">{completedCoursesCount}</span>
                </div>
                
                <div className="flex justify-between items-center p-3 bg-gray-50 rounded-xl">
                  <span className="text-gray-600">Lessons Completed</span>
                  <span className="font-bold text-gray-900 text-lg">{completedLessonsCount}</span>
                </div>
                
                <div className="flex justify-between items-center p-3 bg-gray-50 rounded-xl">
                  <span className="text-gray-600">Total Learning Time</span>
                  <span className="font-bold text-gray-900 text-lg">{formatLearningTime(totalLearningTime)}</span>
                </div>
                
                <div className="flex justify-between items-center p-3 bg-gray-50 rounded-xl">
                  <span className="text-gray-600">Current Streak</span>
                  <span className="font-bold text-gray-900 text-lg flex items-center">
                    <Flame className="w-5 h-5 text-orange-500 mr-1" />
                    {dailyStreak} days
                  </span>
                </div>
                
                <div className="flex justify-between items-center p-3 bg-gray-50 rounded-xl">
                  <span className="text-gray-600">Total Points</span>
                  <span className="font-bold text-gray-900 text-lg">{userPoints.toLocaleString()}</span>
                </div>
                
                <div className="flex justify-between items-center p-3 bg-gray-50 rounded-xl">
                  <span className="text-gray-600">Podcasts Completed</span>
                  <span className="font-bold text-gray-900 text-lg">{completedPodcastsCount}</span>
                </div>
                
                <div className="flex justify-between items-center p-3 bg-gray-50 rounded-xl">
                  <span className="text-gray-600">Avg. Completion</span>
                  <span className="font-bold text-gray-900 text-lg">{avgCompletion.toFixed(1)}%</span>
                </div>
              </div>
            </div>

            {/* Ambassador Program CTA */}
            {accessTier === 'free' && (
              <div className="bg-gradient-to-br from-purple-600 via-purple-500 to-indigo-600 rounded-xl p-6 text-white shadow-xl">
                <div className="flex items-center space-x-2 mb-4">
                  <Crown className="w-6 h-6 text-yellow-300" />
                  <h3 className="text-xl font-bold">Become an Ambassador</h3>
                </div>
                <p className="text-purple-100 text-sm mb-4">
                  Get Saints Pro access for free! Share our content or apply for a scholarship.
                </p>
                <div className="space-y-3">
                  <div className="flex items-center space-x-2 text-sm">
                    <Award className="w-4 h-4 text-yellow-300 flex-shrink-0" />
                    <span>Full Pro access worth R3990/year</span>
                  </div>
                  <div className="flex items-center space-x-2 text-sm">
                    <Users className="w-4 h-4 text-yellow-300 flex-shrink-0" />
                    <span>Join exclusive community</span>
                  </div>
                </div>
                <button
                  onClick={() => navigate('/ambassador-program')}
                  className="w-full mt-6 px-4 py-3 bg-white text-purple-600 rounded-xl font-semibold hover:bg-purple-50 transition-colors duration-200"
                >
                  Apply Now
                </button>
              </div>
            )}

            {/* Daily Goal */}
            <div className="bg-gradient-to-br from-green-500 to-emerald-600 rounded-xl p-6 text-white shadow-xl">
              <h3 className="text-xl font-bold mb-2">Daily Goal</h3>
              <p className="text-green-100 text-sm mb-4">
                {useStore.getState().getDailyLearningTime?.() > 0 
                  ? `You've learned ${formatLearningTime(useStore.getState().getDailyLearningTime())} today!` 
                  : 'Start learning to build your streak!'}
              </p>
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm">Today's progress</span>
                <span className="text-sm font-bold">
                  {Math.min(Math.round((useStore.getState().getDailyLearningTime?.() || 0) / 30 * 100), 100)}%
                </span>
              </div>
              <div className="w-full h-2 bg-white/20 rounded-full overflow-hidden">
                <div 
                  className="h-full bg-white rounded-full transition-all duration-500"
                  style={{ width: `${Math.min(((useStore.getState().getDailyLearningTime?.() || 0) / 30) * 100, 100)}%` }}
                />
              </div>
              <p className="text-xs text-green-100 mt-3">
                🎯 Goal: 30 minutes of learning per day
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Dashboard;